<template>
  <div class="admin-container">
    <div class="admin-header">
      <h2>Events & Applications</h2>
      <div class="admin-nav">
        <router-link to="/admin" class="nav-button">📅 Events</router-link>
        <router-link to="/admin_comics" class="nav-button">🎭 Comics</router-link>
        <router-link to="/admin_rota" class="nav-button">📋 Rota</router-link>
        <button class="logout-button" @click="logout">🚪 Logout</button>
      </div>
    </div>

    <!-- Events List -->
    <section>
      <div class="section-header">
        <h3>Upcoming Events ({{ eventsWithApplications.length }} events)</h3>
        <div class="filter-group">
          <div class="venue-filter">
            <label for="venue-select">Filter by Venue:</label>
            <select id="venue-select" v-model="selectedVenue" class="venue-select">
              <option value="all">All Venues</option>
              <option v-for="venue in activeVenues" :key="venue.id" :value="venue.id">
                {{ venue.venue_name }}
              </option>
            </select>
          </div>
          <div class="venue-filter">
            <label for="status-filter">Filter by Status:</label>
            <select id="status-filter" v-model="selectedStatus" class="venue-select">
              <option value="all">All Statuses</option>
              <option v-for="(value, key) in APPLICATION_STATUSES" :key="key" :value="value">
                {{ getStatusLabel(value) }}
              </option>
            </select>
          </div>
          <label class="toggle-label">
            <input type="checkbox" v-model="showRejected" />
            Show rejected
          </label>
          <label class="toggle-label">
            <input type="checkbox" v-model="hideDuplicateSubmissions" />
            Hide duplicate submissions
          </label>
          <label class="toggle-label">
            <input v-model="updateSpreadsheet" type="checkbox" @change="onUpdateSpreadsheetPrefChange" />
            Update spreadsheet (Sunset & Craic Acts)
          </label>
        </div>
      </div>

      <div v-if="isLoading" class="loading">Loading events and applications...</div>

      <div v-else-if="eventsWithApplications.length === 0" class="no-events">
        No upcoming events found.
      </div>

      <div v-else class="events-list">
        <div
          v-for="event in eventsWithApplications"
          :key="event.id"
          class="event-card"
        >
          <div class="event-header">
            <div class="event-title-group">
              <h4>{{ event.event_name }}</h4>
              <span v-if="event.venues" class="venue-badge">{{ event.venues.venue_name }}</span>
            </div>
            <div class="event-meta">
              <span class="event-date">{{ formatDate(event.event_date) }}</span>
              <span class="application-count">{{ applicationHeaderCount(event) }} applications</span>
              <button
                v-if="hasPendingApplications(event)"
                class="reject-remaining-button"
                @click="rejectRemaining(event)"
                title="Reject all pending applications for this event"
              >
                Reject pending
              </button>
            </div>
          </div>

          <div v-if="event.applications.length === 0" class="no-applications">
            No applications yet for this event.
          </div>

          <div v-else class="applications-grid">
            <div
              v-for="row in getApplicationDisplayRows(event)"
              :key="row.app.id"
              class="application-cluster"
            >
              <div
                v-for="(app, clusterIdx) in appsInCluster(row, event)"
                :key="app.id"
                :id="'admin-application-' + app.id"
                class="application-compact"
                :class="{
                  'application-compact-duplicate': hideDuplicateSubmissions && row.duplicateBadgeCount > 0 && clusterIdx > 0,
                  'application-compact-highlight': highlightApplicationId === app.id,
                }"
              >
              <div class="comedian-header">
                <div class="video-thumbnail-slot">
                  <img
                    v-if="getVideoThumbnail(app.video_link)"
                    :src="getVideoThumbnail(app.video_link)"
                    class="video-thumbnail"
                    :alt="app.comedian_name + ' video'"
                    @click="previewVideo(app)"
                    loading="lazy"
                  />
                  <div
                    v-else
                    class="video-thumbnail-placeholder"
                    aria-hidden="true"
                  />
                </div>
                <div class="comedian-name-status">
                  <div class="comedian-name-block">
                    <strong>{{ app.comedian_name }}</strong>
                    <span class="application-age">{{ formatApplicationAge(app.created_at) }}</span>
                    <span v-if="app.offered_by_email" class="application-booked-by">
                      Booked by {{ app.offered_by_email }}
                    </span>
                  </div>
                  <button
                    v-if="clusterIdx === 0 && row.duplicateBadgeCount > 0"
                    type="button"
                    class="duplicate-expand-badge"
                    :title="duplicateExpansionOpen(event.id, row.app.id) ? 'Hide duplicate submissions' : 'Show duplicate submissions'"
                    @click="toggleDuplicateExpansion(event.id, row.app.id)"
                  >
                    (+{{ row.duplicateBadgeCount }})
                  </button>
                  <select
                    class="status-select"
                    :style="{ borderColor: getStatusColor(app.status), color: getStatusColor(app.status) }"
                    :value="app.status || 'pending'"
                    @change="updateStatus(app, $event.target.value)"
                  >
                    <option v-for="(value, key) in APPLICATION_STATUSES" :key="key" :value="value">
                      {{ getStatusLabel(value) }}
                    </option>
                  </select>
                  <span
                    v-if="app.offer_email_bounced_at"
                    class="undeliverable-flag"
                    :title="app.offer_email_bounce_reason || 'Gmail could not deliver the booking email'"
                  >
                    Undeliverable
                  </span>
                  <select
                    v-if="isRotaCapable(app)"
                    class="slot-type-select"
                    :value="app.slot_type || 'performer'"
                    @change="updateSlotType(app, $event.target.value)"
                  >
                    <option v-for="(label, value) in SLOT_TYPE_LABELS" :key="value" :value="value">
                      {{ label }}
                    </option>
                  </select>
                </div>
                <div class="comedian-actions">
                  <a
                    :href="app.video_link"
                    target="_blank"
                    rel="noopener"
                    class="video-link"
                    title="Watch video (click to preview)"
                    @click.prevent="previewVideo(app)"
                  >
                    🎥
                  </a>
                  <button
                    class="email-button-small"
                    @click="sendEmail(app, event)"
                    title="Send booking email"
                  >
                    📧
                  </button>
                  <button
                    class="block-button"
                    @click="blockComedian(app)"
                    title="Block this comedian"
                  >
                    🚫
                  </button>
                  <button
                    class="details-button"
                    @click="toggleDetails(app)"
                    :title="app.showDetails ? 'Hide details' : 'Show details'"
                  >
                    {{ app.showDetails ? '▼' : '▶' }}
                  </button>
                </div>
              </div>

              <div v-if="app.showDetails" class="comedian-details">
                <div class="detail-row">
                  <span class="label">Email:</span>
                  <span class="value">{{ app.email }}</span>
                </div>
                <div v-if="app.facebook" class="detail-row">
                  <span class="label">Facebook:</span>
                  <a :href="'https://www.facebook.com/search/people/?q=' + encodeURIComponent(app.facebook)" target="_blank" rel="noopener" class="social-link">{{ app.facebook }}</a>
                </div>
                <div v-if="app.instagram" class="detail-row">
                  <span class="label">Instagram:</span>
                  <a :href="'https://instagram.com/' + sanitizeSocialUsername(app.instagram)" target="_blank" rel="noopener" class="social-link">{{ sanitizeSocialUsername(app.instagram) }}</a>
                </div>
                <div v-if="app.notes" class="detail-row">
                  <span class="label">Notes:</span>
                  <span class="value">{{ app.notes }}</span>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>

        <!-- Load More -->
        <div v-if="hasMoreData" class="load-more-container">
          <div v-if="loadingMore" class="loading">Loading more events...</div>
          <div v-else class="load-more-info">
            Scroll down to load more events automatically
          </div>
        </div>
        <div v-else class="end-message">
          All events loaded
        </div>
      </div>
    </section>

    <!-- Summary Stats -->
    <section v-if="eventsWithApplications.length > 0">
      <h3>Quick Stats</h3>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-number">{{ totalApplications }}</div>
          <div class="stat-label">Total Applications</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ eventsWithApps }}</div>
          <div class="stat-label">Events with Applications</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ uniqueComics }}</div>
          <div class="stat-label">Unique Comics</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ averageAppsPerEvent.toFixed(1) }}</div>
          <div class="stat-label">Avg Applications/Event</div>
        </div>
      </div>
    </section>

    <!-- Bulk Actions -->
    <section v-if="eventsWithApplications.length > 0">
      <h3>Actions</h3>
      <div class="input-group">
        <button
          class="primary-button"
          @click="exportEventData"
        >
          📊 Export to CSV
        </button>
        <button
          class="secondary-button"
          @click="refreshData"
        >
          🔄 Refresh Data
        </button>
      </div>
    </section>

    <!-- Blocked List -->
    <section>
      <h3>Blocked Emails ({{ blockedEmailsList.length }})</h3>
      <div v-if="blockedEmailsList.length === 0" class="no-applications">
        No blocked emails.
      </div>
      <div v-else class="blocked-list">
        <div v-for="entry in blockedEmailsList" :key="entry.id" class="blocked-item">
          <span class="blocked-email">{{ entry.email }}</span>
          <span v-if="entry.reason" class="blocked-reason">{{ entry.reason }}</span>
          <button class="unblock-button" @click="unblockEmail(entry)" title="Unblock">
            ✅ Unblock
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
/** @see docs/APP_UI_SPEC.md — C1 (events list), C2 (duplicate-submission display filter), B2 / B4 (booking offer send) */
import { supabase } from '../supabaseClient';
import Swal from 'sweetalert2';
import { logoutAdmin } from '../utils/auth.js';
import { sanitizeSocialUsername } from '../utils/socialLinks.js';
import { getVideoThumbnailUrl } from '../utils/videoEmbed.js';
import { APPLICATION_STATUSES, getStatusLabel, getStatusColor, SLOT_TYPE_LABELS, getSlotTypeLabel } from '../utils/applicationStatus.js';
import {
  rejectOtherPendingApplicationsSameVenueMonth,
  restoreRejectedApplicationsSameVenueMonth,
} from '../utils/applicationVenueMonthExclusivity.js';
import {
  previewVideo as sharedPreviewVideo,
  blockComedian as sharedBlockComedian,
  unblockEmail as sharedUnblockEmail,
} from '../utils/adminActions.js';
import { runBookingOfferSend } from '../utils/bookingOfferSend.js';
import { collapseDuplicateApplicationsByEmail } from '../utils/applicationDedupe.js';
import {
  venuesForAdminDatesFilter,
  readStoredAdminDatesVenue,
  writeStoredAdminDatesVenue,
  resolveAdminDatesVenueSelection,
} from '../utils/adminDatesFilters.js';
import {
  formatApplicationAgeLabel,
  sortApplicationsAlphabetically,
} from '../utils/adminDatesApplications.js';
import {
  buildSheetSyncCandidate,
  formatSheetSyncResultNote,
  readInboxUpdateSpreadsheetPref,
  saveInboxUpdateSpreadsheetPref,
  syncLineupSheetFromInbox,
} from '../utils/sheetLineupSync.js';

export default {
  name: 'AdminDatesPage',
  data() {
    return {
      events: [],
      venues: [],
      comedians: [],
      selectedVenue: 'all',
      selectedStatus: 'all',
      showRejected: false,
      hideDuplicateSubmissions: true,
      duplicateExpansionByKey: {},
      blockedEmails: new Set(),
      blockedEmailsList: [],
      isLoading: true,
      loadingMore: false,
      currentPage: 0,
      pageSize: 20,
      hasMoreData: true,
      emailTemplate: null,
      /** Set from `?highlight_application=` for deep links (e.g. half-claw notify email). */
      highlightApplicationId: null,
      updateSpreadsheet: readInboxUpdateSpreadsheetPref(),
    };
  },
  computed: {
    APPLICATION_STATUSES() {
      return APPLICATION_STATUSES;
    },
    SLOT_TYPE_LABELS() {
      return SLOT_TYPE_LABELS;
    },
    eventsWithApplications() {
      // Filter events to only show those from today onwards
      const today = new Date().toISOString().split('T')[0];
      let filtered = this.events.filter(event => event.event_date >= today);

      const activeVenueIds = new Set(this.activeVenues.map((v) => v.id));
      filtered = filtered.filter((event) => activeVenueIds.has(event.venue_id));

      // Filter by selected venue
      if (this.selectedVenue !== 'all') {
        filtered = filtered.filter(event => event.venue_id == this.selectedVenue);
      }

      // Hide rejected applications unless toggled on
      if (!this.showRejected) {
        filtered = filtered.map(event => ({
          ...event,
          applications: event.applications.filter(app =>
            (app.status || 'pending') !== 'rejected'
          )
        }));
      }

      // Filter by selected status (filter applications within events)
      if (this.selectedStatus !== 'all') {
        filtered = filtered.map(event => ({
          ...event,
          applications: event.applications.filter(app =>
            (app.status || 'pending') === this.selectedStatus
          )
        }));
      }

      return filtered.sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    },

    totalApplications() {
      return this.eventsWithApplications.reduce(
        (sum, event) => sum + event.applications.length, 0
      );
    },

    eventsWithApps() {
      return this.eventsWithApplications.filter(
        event => event.applications.length > 0
      ).length;
    },

    uniqueComics() {
      const comicNames = new Set();
      this.eventsWithApplications.forEach(event => {
        event.applications.forEach(app => {
          comicNames.add(app.comedian_name);
        });
      });
      return comicNames.size;
    },

    averageAppsPerEvent() {
      const eventsWithApps = this.eventsWithApps;
      return eventsWithApps > 0 ? this.totalApplications / eventsWithApps : 0;
    },

    /** Non-archived venues only — F1 `archived_at` from /admin_clubs (C3). */
    activeVenues() {
      return venuesForAdminDatesFilter(this.venues);
    },
  },
  watch: {
    selectedVenue(value) {
      writeStoredAdminDatesVenue(value);
    },
    '$route.query.highlight_application'() {
      void this.applyHighlightFromRouteQuery();
    },
  },
  async created() {
    await Promise.all([
      this.loadBlockedEmails(),
      this.loadVenues(),
      this.loadEmailTemplate(),
      this.loadComedians(),
    ]);
    await this.loadEvents();
    this.setupInfiniteScroll();
    await this.applyHighlightFromRouteQuery();
  },
  beforeUnmount() {
    this.removeInfiniteScroll();
  },
  methods: {
    applyStoredVenueFilter() {
      const activeIds = new Set(this.activeVenues.map((v) => v.id));
      this.selectedVenue = resolveAdminDatesVenueSelection(
        readStoredAdminDatesVenue(),
        activeIds
      );
    },

    async loadBlockedEmails() {
      try {
        const { data, error } = await supabase
          .from('blocked_emails')
          .select('id, email, reason, created_at')
          .order('created_at', { ascending: false });

        if (error) throw error;
        this.blockedEmailsList = data || [];
        this.blockedEmails = new Set(this.blockedEmailsList.map(row => row.email.toLowerCase()));
      } catch (err) {
        console.error('Error loading blocked emails:', err);
      }
    },

    async loadVenues() {
      try {
        const { data, error } = await supabase
          .from('venues')
          .select('*')
          .order('display_order', { ascending: true });

        if (error) throw error;
        this.venues = data || [];
        this.applyStoredVenueFilter();
      } catch (err) {
        console.error('Error loading venues:', err);
      }
    },

    async loadEmailTemplate() {
      try {
        const { data, error } = await supabase
          .from('email_templates')
          .select('*')
          .eq('id', 1)
          .single();

        if (error) {
          console.error('Error loading email template:', error);
          return;
        }
        this.emailTemplate = data;
      } catch (err) {
        console.error('Error loading email template:', err);
      }
    },

    async loadComedians() {
      try {
        const { data, error } = await supabase
          .from('comedians')
          .select('id, email, name, is_mc, is_admin_tech');

        if (error) throw error;
        this.comedians = data || [];
      } catch (err) {
        console.error('Error loading comedians:', err);
      }
    },

    isRotaCapable(app) {
      if (!app.email) return false;
      return this.comedians.some(c =>
        c.email?.toLowerCase() === app.email.toLowerCase() &&
        (c.is_mc || c.is_admin_tech)
      );
    },

    async updateSlotType(app, newSlotType) {
      try {
        const { error } = await supabase
          .from('applications')
          .update({ slot_type: newSlotType })
          .eq('id', app.id);

        if (error) throw error;
        app.slot_type = newSlotType;
      } catch (err) {
        console.error('Error updating slot type:', err);
        Swal.fire('Error', 'Failed to update slot type', 'error');
      }
    },

    getSlotTypeLabel(slotType) { return getSlotTypeLabel(slotType); },

    async loadEvents() {
      this.isLoading = true;
      this.currentPage = 0;
      this.events = [];
      this.hasMoreData = true;

      try {
        await this.fetchEventsPage();
      } catch (err) {
        console.error('Unexpected error:', err);
        Swal.fire('Error', 'An unexpected error occurred', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    async applyHighlightFromRouteQuery() {
      const raw = this.$route?.query?.highlight_application;
      if (raw == null || raw === '') {
        this.highlightApplicationId = null;
        return;
      }
      const id = Number(Array.isArray(raw) ? raw[0] : raw);
      if (!Number.isFinite(id) || id <= 0) {
        this.highlightApplicationId = null;
        return;
      }
      this.highlightApplicationId = id;
      this.selectedStatus = 'all';
      this.showRejected = true;
      await this.ensureEventVisibleForApplication(id);
      await this.$nextTick();
      this.scrollToHighlightApplication(id);
    },

    async ensureEventVisibleForApplication(applicationId) {
      const already = this.events.some(ev =>
        (ev.applications || []).some(a => a.id === applicationId)
      );
      if (already) return;

      const { data: app, error: appErr } = await supabase
        .from('applications')
        .select('*')
        .eq('id', applicationId)
        .maybeSingle();
      if (appErr || !app) return;

      if (this.blockedEmails.has(app.email?.toLowerCase())) return;

      const { data: eventRow, error: evErr } = await supabase
        .from('events')
        .select(`
          *,
          venues (
            id,
            venue_name,
            venue_slug,
            address,
            google_maps_url,
            doors_time,
            show_time,
            show_end_time,
            has_spot_lengths
          )
        `)
        .eq('id', app.event_id)
        .maybeSingle();
      if (evErr || !eventRow) return;

      const { data: appsForEvent, error: appsErr } = await supabase
        .from('applications')
        .select('*')
        .eq('event_id', app.event_id);
      if (appsErr) return;

      const appsList = [];
      (appsForEvent || []).forEach(a => {
        if (this.blockedEmails.has(a.email?.toLowerCase())) return;
        appsList.push({ ...a, showDetails: a.id === applicationId });
      });

      const merged = { ...eventRow, applications: appsList };
      if (!this.events.find(e => e.id === merged.id)) {
        this.events = [...this.events, merged].sort(
          (a, b) => new Date(a.event_date) - new Date(b.event_date)
        );
      }
    },

    scrollToHighlightApplication(id) {
      const el = document.getElementById(`admin-application-${id}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },

    async fetchEventsPage() {
      const startRange = this.currentPage * this.pageSize;
      const endRange = startRange + this.pageSize - 1;

      // Get today's date for filtering future events
      const today = new Date().toISOString().split('T')[0];

      // First, fetch future events with venue info (including address, google_maps_url, and timing for booking emails)
      const { data: events, error: eventsError } = await supabase
        .from('events')
        .select(`
          *,
          venues (
            id,
            venue_name,
            venue_slug,
            address,
            google_maps_url,
            doors_time,
            show_time,
            show_end_time,
            has_spot_lengths
          )
        `)
        .gte('event_date', today)  // Only future events (today and onwards)
        .order('event_date', { ascending: true })
        .range(startRange, endRange);

      if (eventsError) {
        console.error('Error fetching events:', eventsError);
        throw eventsError;
      }

      if (!events || events.length === 0) {
        this.hasMoreData = false;
        return;
      }

      // Get the event IDs for this page
      const eventIds = events.map(e => e.id);

      // Fetch applications for these events separately
      // This query works because we're querying applications directly (RLS allows authenticated users)
      const { data: applications, error: appsError } = await supabase
        .from('applications')
        .select('*')
        .in('event_id', eventIds);

      if (appsError) {
        console.error('Error fetching applications:', appsError);
        // Continue without applications rather than failing completely
      }

      // Group applications by event_id, filtering out blocked emails
      const applicationsByEvent = {};
      (applications || []).forEach(app => {
        if (this.blockedEmails.has(app.email?.toLowerCase())) return;
        if (!applicationsByEvent[app.event_id]) {
          applicationsByEvent[app.event_id] = [];
        }
        applicationsByEvent[app.event_id].push({
          ...app,
          showDetails: false
        });
      });

      // Merge events with their applications (alphabetical within each event)
      const processedEvents = events.map(event => ({
        ...event,
        applications: sortApplicationsAlphabetically(applicationsByEvent[event.id] || []),
      }));

      const existingIds = new Set(this.events.map(e => e.id));
      const deduped = processedEvents.filter(e => !existingIds.has(e.id));
      this.events = [...this.events, ...deduped];

      // Check if we've reached the end
      if (events.length < this.pageSize) {
        this.hasMoreData = false;
      }

      this.currentPage++;
    },

    async loadMoreEvents() {
      if (this.loadingMore || !this.hasMoreData) return;

      this.loadingMore = true;
      try {
        await this.fetchEventsPage();
      } catch (err) {
        console.error('Error loading more events:', err);
        Swal.fire('Error', 'Failed to load more events', 'error');
      } finally {
        this.loadingMore = false;
      }
    },

    async sendEmail(application, event) {
      try {
        await runBookingOfferSend({
          supabase,
          application,
          event,
          emailTemplate: this.emailTemplate,
          applicationId: application.id,
          onOffered: async (spot) => {
            application.offer_spot_code = spot.spotCode;
            application.offer_is_bringer = spot.isBringer;
            await this.updateStatus(application, APPLICATION_STATUSES.OFFERED);
          },
        });
      } catch (error) {
        console.error('Error creating email:', error);
        Swal.fire('Error', 'Failed to create email', 'error');
      }
    },

    toggleDetails(application) {
      application.showDetails = !application.showDetails;
    },

    async refreshData() {
      await this.loadEvents();
      await this.applyHighlightFromRouteQuery();
      Swal.fire('Success', 'Data refreshed!', 'success');
    },

    setupInfiniteScroll() {
      this.handleScroll = () => {
        if (this.loadingMore || !this.hasMoreData) return;

        const scrollPosition = window.innerHeight + window.scrollY;
        const documentHeight = document.documentElement.offsetHeight;

        // Load more when user is within 200px of bottom
        if (scrollPosition >= documentHeight - 200) {
          this.loadMoreEvents();
        }
      };

      window.addEventListener('scroll', this.handleScroll);
    },

    removeInfiniteScroll() {
      if (this.handleScroll) {
        window.removeEventListener('scroll', this.handleScroll);
      }
    },

    exportEventData() {
      try {
        const csvData = [];

        this.eventsWithApplications.forEach(event => {
          if (event.applications.length === 0) {
            // Include events with no applications
            csvData.push({
              'Event Name': event.event_name,
              'Event Date': this.formatDate(event.event_date),
              'Comedian Name': 'No applications',
              'Email': '',
              'Video Link': '',
              'Facebook': '',
              'Instagram': '',
              'Notes': ''
            });
          } else {
            // Include each application
            event.applications.forEach(app => {
              csvData.push({
                'Event Name': event.event_name,
                'Event Date': this.formatDate(event.event_date),
                'Comedian Name': app.comedian_name,
                'Email': app.email,
                'Video Link': app.video_link,
                'Facebook': app.facebook || '',
                'Instagram': app.instagram || '',
                'Notes': app.notes || ''
              });
            });
          }
        });

        const csvString = this.convertToCSV(csvData);
        this.downloadCSV(csvString, 'comedy-events-applications.csv');

      } catch (error) {
        console.error('Error exporting data:', error);
        Swal.fire('Error', 'Failed to export data', 'error');
      }
    },

    convertToCSV(data) {
      if (!data.length) return '';

      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row =>
          headers.map(header => {
            const value = row[header];
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ].join('\n');

      return csvContent;
    },

    downloadCSV(csvContent, filename) {
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    hasPendingApplications(event) {
      return event.applications.some(app => !app.status || app.status === 'pending');
    },

    async rejectRemaining(event) {
      const pendingApps = event.applications.filter(app => !app.status || app.status === 'pending');
      if (pendingApps.length === 0) return;

      const result = await Swal.fire({
        title: `Reject ${pendingApps.length} pending?`,
        text: `This will mark ${pendingApps.length} pending application(s) as rejected for ${event.event_name}.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Yes, reject them'
      });

      if (!result.isConfirmed) return;

      try {
        const ids = pendingApps.map(app => app.id);
        const { error } = await supabase
          .from('applications')
          .update({ status: 'rejected' })
          .in('id', ids);

        if (error) throw error;

        // Update local state
        pendingApps.forEach(app => { app.status = 'rejected'; });

        Swal.fire('Done', `${pendingApps.length} application(s) rejected.`, 'success');
      } catch (err) {
        console.error('Error rejecting applications:', err);
        Swal.fire('Error', 'Failed to reject applications', 'error');
      }
    },

    async updateStatus(application, newStatus) {
      try {
        const { error } = await supabase
          .from('applications')
          .update({ status: newStatus })
          .eq('id', application.id);

        if (error) throw error;
        application.status = newStatus;

        const event = this.events.find(e =>
          e.applications.some(a => a.id === application.id)
        );

        if (newStatus === APPLICATION_STATUSES.OFFERED || newStatus === APPLICATION_STATUSES.CONFIRMED) {
          if (event) {
            await this.rejectOtherApplicationsSameVenueMonth(application, event);
          }
        } else if (newStatus === APPLICATION_STATUSES.PENDING) {
          if (event) {
            await this.restoreOtherApplicationsSameVenueMonth(application, event);
          }
        }

        const sheetAction =
          newStatus === APPLICATION_STATUSES.CONFIRMED
            ? 'confirm'
            : newStatus === APPLICATION_STATUSES.REJECTED
              ? 'reject'
              : null;
        if (sheetAction && this.updateSpreadsheet && event) {
          const sheetResult = await syncLineupSheetFromInbox(supabase, {
            cand: buildSheetSyncCandidate(application, event),
            action: sheetAction,
          });
          const sheetNote = formatSheetSyncResultNote(sheetResult, { enabled: true });
          if (sheetNote) {
            const icon =
              sheetNote.includes('not updated') ||
              sheetNote.includes('no matching') ||
              sheetNote.includes('not configured')
                ? 'warning'
                : 'success';
            Swal.fire('Updated', sheetNote, icon);
          }
        }
      } catch (err) {
        console.error('Error updating status:', err);
        Swal.fire('Error', 'Failed to update application status', 'error');
      }
    },

    onUpdateSpreadsheetPrefChange() {
      saveInboxUpdateSpreadsheetPref(this.updateSpreadsheet);
    },

    async rejectOtherApplicationsSameVenueMonth(offeredApp, offeredEvent) {
      try {
        const rejected = await rejectOtherPendingApplicationsSameVenueMonth(
          supabase,
          offeredApp,
          offeredEvent
        );
        if (rejected && rejected.length > 0) {
          const rejectedIds = new Set(rejected.map((r) => r.id));
          this.events.forEach((event) => {
            event.applications.forEach((app) => {
              if (rejectedIds.has(app.id)) {
                app.status = APPLICATION_STATUSES.REJECTED;
              }
            });
          });
        }
      } catch (err) {
        console.error('Error rejecting other applications:', err);
      }
    },

    async restoreOtherApplicationsSameVenueMonth(restoredApp, restoredEvent) {
      try {
        const restored = await restoreRejectedApplicationsSameVenueMonth(
          supabase,
          restoredApp,
          restoredEvent
        );
        if (restored && restored.length > 0) {
          const restoredIds = new Set(restored.map((r) => r.id));
          this.events.forEach((event) => {
            event.applications.forEach((app) => {
              if (restoredIds.has(app.id)) {
                app.status = APPLICATION_STATUSES.PENDING;
              }
            });
          });
        }
      } catch (err) {
        console.error('Error restoring other applications:', err);
      }
    },

    getStatusLabel(status) {
      return getStatusLabel(status);
    },

    getStatusColor(status) {
      return getStatusColor(status);
    },

    logout() {
      logoutAdmin();
      this.$router.push('/');
    },

    getVideoThumbnail(url) {
      return getVideoThumbnailUrl(url);
    },

    previewVideo(application) {
      sharedPreviewVideo(application);
    },

    async blockComedian(app) {
      const { blocked } = await sharedBlockComedian(app);
      if (blocked) {
        this.blockedEmails.add(app.email.toLowerCase());
        await this.loadBlockedEmails();
        this.events.forEach(event => {
          event.applications = event.applications.filter(
            a => a.email?.toLowerCase() !== app.email.toLowerCase()
          );
        });
      }
    },

    async unblockEmail(entry) {
      const { unblocked } = await sharedUnblockEmail(entry);
      if (unblocked) {
        await this.loadBlockedEmails();
        await this.loadEvents();
      }
    },

    sanitizeSocialUsername(username) {
      return sanitizeSocialUsername(username);
    },

    duplicateExpansionKey(eventId, anchorAppId) {
      return `${eventId}:${anchorAppId}`;
    },

    duplicateExpansionOpen(eventId, anchorAppId) {
      return !!this.duplicateExpansionByKey[this.duplicateExpansionKey(eventId, anchorAppId)];
    },

    toggleDuplicateExpansion(eventId, anchorAppId) {
      const key = this.duplicateExpansionKey(eventId, anchorAppId);
      const next = { ...this.duplicateExpansionByKey };
      if (next[key]) delete next[key];
      else next[key] = true;
      this.duplicateExpansionByKey = next;
    },

    getApplicationDisplayRows(event) {
      const apps = event.applications;
      if (!this.hideDuplicateSubmissions) {
        return apps.map((app) => ({
          app,
          duplicateBadgeCount: 0,
          hiddenDuplicates: [],
        }));
      }
      const { visible, hiddenCountByApplicationId, hiddenDuplicatesByVisibleId } =
        collapseDuplicateApplicationsByEmail(apps);
      return visible.map((app) => ({
        app,
        duplicateBadgeCount: hiddenCountByApplicationId[app.id] || 0,
        hiddenDuplicates: hiddenDuplicatesByVisibleId[app.id] || [],
      }));
    },

    appsInCluster(row, event) {
      const primary = [row.app];
      if (!this.hideDuplicateSubmissions || !row.duplicateBadgeCount) return primary;
      if (!this.duplicateExpansionOpen(event.id, row.app.id)) return primary;
      return [...primary, ...row.hiddenDuplicates];
    },

    applicationHeaderCount(event) {
      if (!this.hideDuplicateSubmissions) return event.applications.length;
      const { visible } = collapseDuplicateApplicationsByEmail(event.applications);
      return visible.length;
    },

    formatApplicationAge(createdAt) {
      return formatApplicationAgeLabel(createdAt);
    },
  }
};
</script>

<style scoped>
.admin-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.admin-header h2 {
  margin: 0;
  color: #333;
}

.admin-nav {
  display: flex;
  gap: 10px;
  align-items: center;
}

.nav-button {
  padding: 8px 16px;
  background: #6c757d;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 14px;
  transition: background-color 0.2s;
}

.nav-button:hover {
  background: #5a6268;
}

.nav-button.router-link-active {
  background: #007bff;
}

.logout-button {
  padding: 8px 16px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.logout-button:hover {
  background: #c82333;
}

section {
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.venue-filter {
  display: flex;
  align-items: center;
  gap: 10px;
}

.venue-filter label {
  font-weight: 600;
  color: #666;
}

.venue-select {
  padding: 8px 12px;
  border: 2px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s;
}

.venue-select:hover {
  border-color: #007bff;
}

.venue-select:focus {
  outline: none;
  border-color: #007bff;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
}

.no-events {
  text-align: center;
  padding: 40px;
  color: #666;
}

.events-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.event-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background: #fafafa;
}

.event-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.event-title-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.event-header h4 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.venue-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.event-meta {
  display: flex;
  gap: 15px;
  align-items: center;
}

.event-date {
  font-weight: bold;
  color: #007bff;
}

.application-count {
  background: #e9ecef;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  color: #6c757d;
}

.no-applications {
  color: #666;
  font-style: italic;
  padding: 20px;
  text-align: center;
}

.applications-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
  align-items: stretch;
}

.application-cluster {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  height: 100%;
}

.application-cluster > .application-compact:first-of-type {
  flex: 1 1 auto;
}

.application-cluster > .application-compact.application-compact-duplicate {
  flex: 0 0 auto;
}

.application-compact-duplicate {
  margin-left: 12px;
  border-left: 3px solid #dee2e6;
  padding-left: 10px;
  background: #f9fafb;
}

.application-compact-highlight {
  outline: 3px solid #2563eb;
  outline-offset: 3px;
  border-radius: 6px;
}

.duplicate-expand-badge {
  padding: 2px 8px;
  margin-left: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #495057;
  background: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 10px;
  cursor: pointer;
  vertical-align: middle;
}

.duplicate-expand-badge:hover {
  background: #dee2e6;
  border-color: #adb5bd;
}

.application-compact {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 12px;
  background: white;
  display: flex;
  flex-direction: column;
  min-height: 128px;
  box-sizing: border-box;
}

.comedian-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 10px;
}

.video-thumbnail-slot {
  width: 80px;
  height: 45px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
}

.video-thumbnail {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.video-thumbnail-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  box-sizing: border-box;
  background: linear-gradient(145deg, #eceff1 0%, #dfe3e6 100%);
  border: 1px dashed #ced4da;
}

.video-thumbnail:hover {
  opacity: 0.85;
  transform: scale(1.05);
}

.comedian-actions {
  display: flex;
  gap: 5px;
  flex-shrink: 0;
  align-self: flex-start;
}

.video-link {
  padding: 4px 8px;
  background: #dc3545;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 12px;
}

.video-link:hover {
  background: #c82333;
  color: white;
}

.email-button-small {
  padding: 4px 8px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.email-button-small:hover {
  background: #0056b3;
}

.details-button {
  padding: 4px 8px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.details-button:hover {
  background: #545b62;
}

.comedian-details {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #eee;
}

.detail-row {
  display: flex;
  margin-bottom: 4px;
  font-size: 12px;
}

.label {
  font-weight: bold;
  min-width: 70px;
  color: #666;
}

.value {
  color: #333;
  word-break: break-word;
}

.social-link {
  color: #007bff;
  text-decoration: none;
  word-break: break-word;
}

.social-link:hover {
  text-decoration: underline;
  color: #0056b3;
}

.filter-group {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  align-items: center;
}

.reject-remaining-button {
  padding: 4px 10px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  transition: background-color 0.2s;
}

.reject-remaining-button:hover {
  background: #c82333;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  white-space: nowrap;
}

.comedian-name-status {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
}

.comedian-name-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.application-age {
  font-size: 11px;
  font-weight: normal;
  color: #6c757d;
  line-height: 1.2;
}

.application-booked-by {
  font-size: 11px;
  font-weight: normal;
  color: #495057;
  line-height: 1.2;
}

.status-select {
  padding: 2px 6px;
  border: 2px solid #6c757d;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
}

.undeliverable-flag {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #842029;
  background: #f8d7da;
  border: 1px solid #f5c2c7;
  white-space: nowrap;
}

.status-select:hover {
  opacity: 0.8;
}

.status-select:focus {
  outline: none;
}

.slot-type-select {
  padding: 2px 6px;
  border: 2px solid #17a2b8;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: white;
  cursor: pointer;
  color: #17a2b8;
}

.slot-type-select:focus {
  outline: none;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.stat-card {
  text-align: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #007bff;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.input-group {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.primary-button {
  padding: 10px 20px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.primary-button:hover {
  background: #0056b3;
}

.secondary-button {
  padding: 10px 20px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.secondary-button:hover {
  background: #545b62;
}

.load-more-container {
  text-align: center;
  padding: 20px;
}

.load-more-info {
  color: #666;
  font-style: italic;
}

.end-message {
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
  background: #f8f9fa;
  border-radius: 4px;
}

.block-button {
  padding: 4px 8px;
  background: #c8cbce;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.block-button:hover {
  background: #dc3545;
}

.blocked-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.blocked-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #fff3f3;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
}

.blocked-email {
  font-weight: 600;
  color: #721c24;
}

.blocked-reason {
  color: #856404;
  font-size: 13px;
  font-style: italic;
}

.unblock-button {
  margin-left: auto;
  padding: 4px 12px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.unblock-button:hover {
  background: #218838;
}

@media (max-width: 768px) {
  .admin-header {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }

  .admin-nav {
    flex-wrap: wrap;
    justify-content: center;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .venue-filter {
    width: 100%;
  }

  .venue-select {
    flex: 1;
  }

  .applications-grid {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .event-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .event-title-group {
    flex-direction: column;
    align-items: flex-start;
  }

  .event-meta {
    align-self: stretch;
    justify-content: space-between;
  }
}
</style>




